/**
 * Lunburg behavior-layer events (B-DOM + B-Observer foundation).
 *
 * Theme-side publisher for storefront behavior signals. Theme JS detects DOM
 * behavior, then publishes 'lunburg:<event_name>' on the Shopify Custom Pixel
 * channel via Shopify.analytics.publish. The Custom Pixel subscribes and
 * forwards to the canonical event pipeline (sGTM / GA4 / CAPI).
 *
 * See projects/new_gen_events_platform/event-catalog/A4-events-data-attribute-mapping.md
 * §3.15 for the full Behavior Layer plan and contract.
 *
 * Wave 1 scope (B-DOM):
 *   1. publishLunburgEvent(name, payload) helper.
 *   2. locale_changed: change on select[name="combined_localization"].
 *   3. cart_note_added: blur on textarea[name="note"] when value changed.
 *
 * Wave 2 scope (B-Observer):
 *   4. Engagement engine: visibility + focus + recent-input (5s per A3 §2.10)
 *      with 1Hz heartbeat. Per-page-type thresholds via A3 logical taxonomy.
 *   5. engaged_page_view: fires once per page per tab session when threshold met.
 *      PDP extension (2026-06-08): on product pages, the event also fires at a
 *      lower 20s engaged-time threshold (interaction_engaged_ms) once the
 *      shopper advances a gallery photo or expands a product-section accordion;
 *      and additionally fires when the shopper clicks a product variant swatch
 *      (combined-listing link that navigates — carried to the next page load via
 *      sessionStorage). See the "PDP engaged_page_view interaction gates" and
 *      "PDP variant-click trigger" blocks below.
 *   6. scroll_depth: 25/50/75 milestones via passive scroll listener.
 *
 * Wave 3 scope (B-Observer):
 *   7. engaged_accordion_open: delegated <details> toggle listener + per-accordion
 *      DUAL gate: (a) a.engaged_ms >= 3000 (tick-counter, engaged time) AND
 *      (b) (Date.now() - a.opened_ts) >= 3000 (wall-clock since open). The
 *      wall-clock guard ensures the emitted time_open_ms is never < 3000 even
 *      when the heartbeat tick lands just after open (tick counter would
 *      otherwise over-count by up to ~1s). Reads data-lb-accordion-id / -label
 *      / -class. engagement_time_ms intentionally suppressed (dwell carried
 *      by time_open_ms).
 *   8. engaged_section_view: IntersectionObserver on [data-lb-section-name] +
 *      ≥50% visible + 3s engaged-dwell gate. Section labels emitted from
 *      LunburgAnalyticsDomContext.section_names map via suffix-selector
 *      attribute application at module init.
 *
 * Future waves: forms, leads, narrow CTAs, drawer (Wave 4); select_item (Wave 5);
 * configurator_interact / gallery_interact (Wave 6).
 *
 * Debug toggle: set `window.LunburgAnalyticsDebug = true` in the console BEFORE
 * an event fires to log every publish via console.log. Useful pre-pixel-wire
 * for verifying the publisher side without a subscriber on the channel.
 */
(function () {
  'use strict';

  var EVENT_VERSION = 'b-v1';
  var EVENT_SOURCE = 'theme_behavior';

  /**
   * Publish a Lunburg custom event to the Shopify Custom Pixel channel.
   * No-ops if Shopify.analytics is not yet available (very early page lifecycle).
   * Never throws; analytics path must not break the storefront.
   */
  function publishLunburgEvent(name, payload) {
    try {
      if (
        !window.Shopify ||
        !window.Shopify.analytics ||
        typeof window.Shopify.analytics.publish !== 'function'
      ) {
        return;
      }
      var enriched = Object.assign(
        { event_version: EVENT_VERSION, event_source: EVENT_SOURCE },
        payload || {}
      );
      window.Shopify.analytics.publish('lunburg:' + name, enriched);
      if (window.LunburgAnalyticsDebug) {
        // eslint-disable-next-line no-console
        console.log('[lunburg behavior-events] published', 'lunburg:' + name, enriched);
      }
    } catch (err) {
      if (window.LunburgAnalyticsDebug) {
        // eslint-disable-next-line no-console
        console.warn('[lunburg behavior-events] publish failed', name, err);
      }
    }
  }
  window.publishLunburgEvent = publishLunburgEvent;

  /* ---------- client_error (Tier 6, JS slice only) ------------------------ */
  // A3 §2.9: GA4-only diagnostic event, sample-throttled at ~10 unique errors
  // per session. This slice handles error_class='js' only — window.onerror
  // (uncaught exceptions) and window.unhandledrejection (rejected promises).
  // Resource errors, network errors, and pixel_sandbox errors are out of
  // scope for this slice.
  //
  // Dedup: signature = "js|<message>|<source>|<line>". Same signature in
  // sessionStorage = drop. Once 10 unique signatures have fired in this
  // session, all subsequent JS errors drop. Throttle state lives under its
  // own sessionStorage key, separate from DEDUP_KEY above.

  var CLIENT_ERROR_STATE_KEY = 'lunburg_client_error_state';
  var CLIENT_ERROR_MAX_UNIQUE = 10;        // A3 §2.9
  var CLIENT_ERROR_MSG_MAX_CHARS = 200;
  var CLIENT_ERROR_SOURCE_MAX_CHARS = 256;

  function truncateForClientError(value, max) {
    if (typeof value !== 'string') return value;
    return value.length > max ? value.slice(0, max) : value;
  }

  function readClientErrorState() {
    try {
      var raw = sessionStorage.getItem(CLIENT_ERROR_STATE_KEY);
      if (!raw) return { signatures: [], count: 0 };
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.signatures) || typeof parsed.count !== 'number') {
        return { signatures: [], count: 0 };
      }
      return parsed;
    } catch (err) {
      return { signatures: [], count: 0 };
    }
  }

  function writeClientErrorState(state) {
    try {
      sessionStorage.setItem(CLIENT_ERROR_STATE_KEY, JSON.stringify(state));
    } catch (err) {
      /* ignore */
    }
  }

  function recordClientErrorJs(message, source, line) {
    try {
      var rawMsg = typeof message === 'string'
        ? message
        : String(message == null ? '' : message);
      var msg = truncateForClientError(rawMsg, CLIENT_ERROR_MSG_MAX_CHARS);
      var src = typeof source === 'string' && source.length > 0
        ? truncateForClientError(source, CLIENT_ERROR_SOURCE_MAX_CHARS)
        : null;
      var ln = typeof line === 'number' && isFinite(line) ? line : null;
      var sig = 'js|' + msg + '|' + (src || '') + '|' + (ln == null ? '' : ln);
      var state = readClientErrorState();
      if (state.signatures.indexOf(sig) !== -1) return;
      if (state.count >= CLIENT_ERROR_MAX_UNIQUE) return;
      state.signatures.push(sig);
      state.count += 1;
      writeClientErrorState(state);
      publishLunburgEvent('client_error', {
        error_class: 'js',
        error_message: msg,
        error_source: src,
        error_line: ln
      });
    } catch (err) {
      // Never throw out of the error handler itself.
      if (window.LunburgAnalyticsDebug) {
        // eslint-disable-next-line no-console
        console.warn('[lunburg behavior-events] recordClientErrorJs failed', err);
      }
    }
  }

  // window 'error' — capture phase. Filter out resource errors:
  // <img>/<script>/<link> failures fire with e.target as an Element and no
  // e.error. JS runtime errors fire with e.target === window. Out of scope
  // for this slice = drop element-target errors.
  window.addEventListener('error', function (e) {
    if (!e) return;
    var tgt = e.target;
    var isResourceError = tgt && tgt !== window && (tgt instanceof Element);
    if (isResourceError) return;
    recordClientErrorJs(e.message, e.filename, e.lineno);
  }, true);

  // window 'unhandledrejection'. e.reason can be anything; normalize.
  // Error objects on most browsers do not carry fileName/lineNumber, so
  // error_source / error_line will commonly be null for rejections.
  window.addEventListener('unhandledrejection', function (e) {
    if (!e) return;
    var reason = e.reason;
    var msg;
    var src = null;
    var ln = null;
    if (reason && typeof reason === 'object') {
      if (typeof reason.message === 'string') msg = reason.message;
      else msg = String(reason);
      if (typeof reason.fileName === 'string') src = reason.fileName;
      if (typeof reason.lineNumber === 'number') ln = reason.lineNumber;
    } else {
      msg = String(reason == null ? 'unknown' : reason);
    }
    recordClientErrorJs(msg, src, ln);
  });

  /* ---------- locale_changed (Wave 1) ------------------------------------- */
  // Trigger: change on select[name="combined_localization"].
  // Surface: site-wide localization picker (header / footer).
  // Value format: "<COUNTRY>|<LANG>" e.g. "AT|en".
  // Currency map provided by snippets/lunburg-analytics-dom-context.liquid.
  // Note: the Shopify localization form auto-submits on change, so the
  // outbound publish runs in a tight window before page navigation. Some
  // events may not survive the navigation; future hardening can use
  // sendBeacon or sessionStorage carry-over.

  function parseCombinedLocale(value) {
    if (typeof value !== 'string' || value.indexOf('|') === -1) return null;
    var parts = value.split('|');
    return { country: parts[0] || '', locale: parts[1] || '' };
  }

  function lookupCurrency(countryIso) {
    var ctx = window.LunburgAnalyticsDomContext;
    if (!ctx || !ctx.currency_map) return null;
    return ctx.currency_map[countryIso] || null;
  }

  // Capture pre-change value via focusin so we can emit "from" / "to".
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.tagName === 'SELECT' && t.name === 'combined_localization') {
      t.dataset.lbPrevValue = t.value;
    }
  }, true);

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'SELECT' || t.name !== 'combined_localization') return;
    var fromRaw = t.dataset.lbPrevValue || '';
    var toRaw = t.value || '';
    var fromParsed = parseCombinedLocale(fromRaw);
    var toParsed = parseCombinedLocale(toRaw);
    if (!fromParsed || !toParsed) return;
    publishLunburgEvent('locale_changed', {
      from_locale: fromParsed.locale,
      to_locale: toParsed.locale,
      from_country: fromParsed.country,
      to_country: toParsed.country,
      from_currency: lookupCurrency(fromParsed.country),
      to_currency: lookupCurrency(toParsed.country)
    });
  }, true);

  /* ---------- cart_note_added (Wave 1) ------------------------------------ */
  // Trigger: blur on textarea[name="note"] when value differs from the
  // last-published value for the same node.

  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.tagName === 'TEXTAREA' && t.name === 'note') {
      t.dataset.lbInitialValue = t.value;
    }
  }, true);

  document.addEventListener('blur', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'TEXTAREA' || t.name !== 'note') return;
    var initial = t.dataset.lbInitialValue || '';
    var current = t.value || '';
    if (current === initial) return;
    publishLunburgEvent('cart_note_added', {
      note_length: current.length
    });
    t.dataset.lbInitialValue = current;
  }, true);

  /* ---------- Engagement engine (Wave 2 + Wave 3, B-Observer) ------------- */
  // Per A3 §2.10:229 invariant:
  //   "Engaged time" = time accumulated only while:
  //     - tab is visible (document.visibilityState === 'visible')
  //     - window is focused (document.hasFocus())
  //     - user had an input event in the last 5 SECONDS
  //
  // Wave 2 thresholds (engaged_page_view) keyed on A3 logical engagement_page_type.
  // Wave 3 thresholds (engaged_accordion_open, engaged_section_view) are fixed
  // 3-second engaged dwells per A4 §4.4 / §4.5, gated by the same invariant.
  //
  // engaged_accordion_open uses a DUAL gate: tick-counted engaged_ms >= 3000
  // AND wall-clock-since-open >= 3000. The wall-clock guard prevents the
  // tick-counter's coarse +1000ms-per-tick increment from firing the event
  // before the user has actually spent 3 wall seconds on the open accordion.
  //
  // Dedup token shape:
  //   engaged_page_view:       (href, 'engaged_page_view')
  //   engaged_accordion_open:  (href, 'engaged_accordion_open', accordion_id)
  //   engaged_section_view:    (href, 'engaged_section_view',   section_id)
  // Tab-scoped sessionStorage. Cross-tab dedup is intentionally out of scope.

  var RECENT_INPUT_WINDOW_MS = 5000;       // A3 §2.10:229
  var SECTION_DWELL_MS = 3000;             // A4 §4.5
  var ACCORDION_DWELL_MS = 3000;           // A4 §4.4
  var SECTION_VISIBLE_THRESHOLD_PCT = 50;  // A4 §4.5 — ≥50% in viewport

  // interaction_engaged_ms (pdp only): the lower engaged-time threshold at which
  // engaged_page_view may fire IF a gallery-photo advance or accordion expand
  // has occurred. Other page types have no interaction path (key absent).
  var ENG_THRESHOLDS = {
    pdp:             { engaged_ms: 30000, scroll_pct: 50, interaction_engaged_ms: 20000 },
    plp:             { engaged_ms: 30000, scroll_pct: 50 },
    editorial:       { engaged_ms: 30000, scroll_pct: 50 },
    journal_article: { engaged_ms: 30000, scroll_pct: 50 },
    trust:           { engaged_ms: 15000, scroll_pct: 25 },
    mission:         { engaged_ms: 15000, scroll_pct: 25 },
    default:         { engaged_ms: 15000, scroll_pct: 25 }
  };

  var engineState = {
    engaged_time_ms: 0,
    max_scroll_depth: 0,
    last_input_ts: 0, // 0 = no input yet; engine will NOT accumulate until first input
    page_view_fired: false,
    last_scroll_milestone_emitted: -1,
    page_loaded_at: Date.now(),
    // PDP engaged_page_view interaction gates (2026-06-08 extension):
    pdp_carousel_advanced: false, // user advanced the gallery to a different photo
    pdp_accordion_opened: false,  // a product-section accordion (.accordion__disclosure) was expanded
    // Wave 3 additions:
    observed_sections: {}, // section_id (JSON-key suffix) -> { engaged_ms, last_visible_pct, section_name, fired }
    open_accordions: {}    // accordion_id -> { opened_ts, engaged_ms, label, class, fired }
  };

  function currentEngagementPageType() {
    var ctx = window.LunburgAnalyticsDomContext;
    if (ctx && typeof ctx.engagement_page_type === 'string') return ctx.engagement_page_type;
    return 'default';
  }

  function thresholdsForCurrentPage() {
    return ENG_THRESHOLDS[currentEngagementPageType()] || ENG_THRESHOLDS.default;
  }

  function recordRecentInput() {
    engineState.last_input_ts = Date.now();
  }
  // capture:true so these fire even if downstream stops propagation
  document.addEventListener('scroll', recordRecentInput, { passive: true, capture: true });
  document.addEventListener('mousemove', recordRecentInput, { passive: true, capture: true });
  document.addEventListener('keydown', recordRecentInput, { passive: true, capture: true });
  document.addEventListener('touchstart', recordRecentInput, { passive: true, capture: true });

  function isCurrentlyEngaged() {
    if (document.visibilityState !== 'visible') return false;
    if (!document.hasFocus()) return false;
    if (engineState.last_input_ts === 0) return false;
    if (Date.now() - engineState.last_input_ts > RECENT_INPUT_WINDOW_MS) return false;
    return true;
  }

  function scrollDepthPct() {
    var doc = document.documentElement;
    var scrolled = window.scrollY || doc.scrollTop || 0;
    var viewport = window.innerHeight || doc.clientHeight || 0;
    var total = doc.scrollHeight || 0;
    if (total <= viewport) return 100;
    return Math.min(100, Math.round((scrolled + viewport) / total * 100));
  }

  function updateScrollDepth() {
    var d = scrollDepthPct();
    if (d > engineState.max_scroll_depth) engineState.max_scroll_depth = d;
    return d;
  }

  /* scroll_depth: 25 / 50 / 75 milestones */
  window.addEventListener('scroll', function () {
    var d = updateScrollDepth();
    var milestones = [25, 50, 75];
    for (var i = 0; i < milestones.length; i++) {
      var m = milestones[i];
      if (d >= m && engineState.last_scroll_milestone_emitted < m) {
        engineState.last_scroll_milestone_emitted = m;
        publishLunburgEvent('scroll_depth', {
          scroll_depth_at_event: m,
          scroll_direction: 'down',
          time_on_page_at_event: Date.now() - engineState.page_loaded_at
        });
      }
    }
  }, { passive: true });

  // Refresh scroll-depth on resize too (viewport change affects depth calc).
  window.addEventListener('resize', updateScrollDepth, { passive: true });

  /* Behavior-layer dedup (tab-scoped via sessionStorage). */
  var DEDUP_KEY = 'lunburg_behavior_engaged_fired';

  function dedupToken(eventName, id) {
    return window.location.href + '|' + eventName + (id ? '|' + id : '');
  }

  function alreadyFired(eventName, id) {
    try {
      var raw = sessionStorage.getItem(DEDUP_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return arr.indexOf(dedupToken(eventName, id)) !== -1;
    } catch (err) {
      return false;
    }
  }

  function markFired(eventName, id) {
    try {
      var raw = sessionStorage.getItem(DEDUP_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      var token = dedupToken(eventName, id);
      if (arr.indexOf(token) === -1) arr.push(token);
      sessionStorage.setItem(DEDUP_KEY, JSON.stringify(arr));
    } catch (err) {
      /* ignore */
    }
  }

  /* ---------- Wave 3: section-name attribute application + IO ------------- */
  // Read LunburgAnalyticsDomContext.section_names map (per-template, populated
  // by snippets/lunburg-analytics-dom-context.liquid), apply data-lb-section-name
  // attribute to each matched <div id="shopify-section-template--*__<key>">
  // element via suffix selector. Then register the IntersectionObserver on every
  // [data-lb-section-name] element so the heartbeat can track per-section dwell.

  function applySectionNames() {
    var ctx = window.LunburgAnalyticsDomContext;
    if (!ctx || !ctx.section_names || typeof ctx.section_names !== 'object') return 0;
    var applied = 0;
    for (var key in ctx.section_names) {
      if (!Object.prototype.hasOwnProperty.call(ctx.section_names, key)) continue;
      var label = ctx.section_names[key];
      if (!label || typeof label !== 'string') continue;
      // Sanitize key for CSS selector — keys are JSON-keys (alphanumeric + underscore),
      // so attribute suffix-match is safe. Defensive double-quote strip.
      var safeKey = key.replace(/"/g, '');
      var selector = '[id$="__' + safeKey + '"]';
      var el;
      try {
        el = document.querySelector(selector);
      } catch (err) {
        continue;
      }
      if (!el) continue;
      if (el.hasAttribute('data-lb-section-name')) continue;
      el.setAttribute('data-lb-section-name', label);
      applied++;
    }
    return applied;
  }

  function registerSectionObservers() {
    if (typeof IntersectionObserver === 'undefined') return 0;
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var el = entry.target;
        var name = el.getAttribute('data-lb-section-name');
        if (!name) continue;
        // Extract section_id (JSON-key suffix) from the rendered DOM id.
        // Shopify format: shopify-section-template--<num>__<json-key>
        var domId = el.id || '';
        var sid = domId.indexOf('__') !== -1 ? domId.split('__').pop() : domId;
        if (!sid) continue;
        var state = engineState.observed_sections[sid] || {
          engaged_ms: 0,
          last_visible_pct: 0,
          section_name: name,
          fired: false
        };
        state.last_visible_pct = Math.round(entry.intersectionRatio * 100);
        state.section_name = name;
        engineState.observed_sections[sid] = state;
      }
    }, { threshold: [0.5] });
    var nodes = document.querySelectorAll('[data-lb-section-name]');
    for (var j = 0; j < nodes.length; j++) {
      observer.observe(nodes[j]);
    }
    return nodes.length;
  }

  // Apply attributes + register observers once the DOM is parseable. The
  // script is loaded with defer so DOMContentLoaded has either fired or is
  // about to fire. Guard against both states.
  function initSectionTracking() {
    var applied = applySectionNames();
    var observed = registerSectionObservers();
    if (window.LunburgAnalyticsDebug) {
      // eslint-disable-next-line no-console
      console.log(
        '[lunburg behavior-events] Wave 3 section tracking init',
        'applied=' + applied,
        'observed=' + observed
      );
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSectionTracking);
  } else {
    initSectionTracking();
  }

  /* ---------- Wave 3: delegated <details> toggle listener ----------------- */
  // Captures open/close on any <details data-lb-accordion-id> in the DOM.
  // On open: start tracking (skip if already fired this session).
  // On close: clear in-flight tracking if not yet fired (sessionStorage dedup
  // ensures a re-open after fire is a no-op).

  document.addEventListener('toggle', function (e) {
    var target = e.target;
    if (!target || target.tagName !== 'DETAILS') return;
    var aid = target.getAttribute('data-lb-accordion-id');
    var alabel = target.getAttribute('data-lb-accordion-label');
    var aclass = target.getAttribute('data-lb-accordion-class');
    if (!aid || !alabel || !aclass) return;
    if (target.open) {
      if (alreadyFired('engaged_accordion_open', aid)) return;
      var existing = engineState.open_accordions[aid];
      if (existing && !existing.fired) {
        existing.opened_ts = Date.now();
        return;
      }
      engineState.open_accordions[aid] = {
        opened_ts: Date.now(),
        engaged_ms: 0,
        label: alabel,
        class: aclass,
        fired: false
      };
    } else {
      var s = engineState.open_accordions[aid];
      if (s && !s.fired) {
        delete engineState.open_accordions[aid];
      }
    }
  }, true);

  /* ---------- PDP engaged_page_view interaction gates (2026-06-08) -------- */
  // Extends engaged_page_view on PRODUCT pages only. In addition to the 30s
  // engaged-time path, the event may fire at the lower interaction_engaged_ms
  // (20s) threshold once the shopper has EITHER advanced the product gallery to
  // a different photo OR expanded a product-section accordion.
  //
  // Carousel ("photo scroll") detection: the gallery custom element
  // <product-gallery><scroll-carousel> dispatches a non-bubbling 'carousel:change'
  // on EVERY active-slide change — including programmatic variant-driven
  // auto-scroll and the initial render, neither of which must count. We gate on
  // a real pointerdown/keydown that occurred INSIDE <product-gallery> within the
  // last GALLERY_INTERACTION_WINDOW_MS. Every gallery control (swipe surface,
  // arrows, thumbnails, dots) lives inside <product-gallery>; the variant
  // selectors do NOT — so variant auto-scroll is excluded by construction, with
  // no dependence on event timing.
  //
  // Rerender-safety: a plain variant change takes <product-rerender>'s partial
  // path and PRESERVES the gallery (it is merely scrolled), but a product-change
  // full-replace rebuilds <scroll-carousel> and drops its listener. The
  // interaction and accordion listeners below are delegated at document level
  // (immune to rerender). 'carousel:change' is (re)bound idempotently via a
  // MutationObserver on the PDP section root.

  var galleryInteractionTs = 0;
  var GALLERY_INTERACTION_WINDOW_MS = 1200;

  function noteGalleryInteraction(e) {
    var t = e.target;
    if (t && typeof t.closest === 'function' && t.closest('product-gallery')) {
      galleryInteractionTs = Date.now();
    }
  }

  function onGalleryCarouselChange() {
    if (engineState.pdp_carousel_advanced) return;             // one-shot
    if (galleryInteractionTs === 0) return;                    // initial render / programmatic
    if (Date.now() - galleryInteractionTs > GALLERY_INTERACTION_WINDOW_MS) return; // not user-driven
    engineState.pdp_carousel_advanced = true;
  }

  function wireGalleryCarousels() {
    var els = document.querySelectorAll('product-gallery scroll-carousel:not([data-lb-cev-wired])');
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute('data-lb-cev-wired', '');
      els[i].addEventListener('carousel:change', onGalleryCarouselChange);
    }
  }

  // A PRODUCT-SECTION accordion expand sets the accordion gate. Scoped to the
  // content disclosures (class 'accordion__disclosure') that live in the SAME
  // Shopify section as the product gallery — i.e. the ~9 accordions beside the
  // product info (Description, The Inscription, Craftsmanship Specification,
  // Material Composition, etc.). This deliberately EXCLUDES header menu
  // disclosures, the footer's responsive column collapsibles (which auto-open
  // at desktop width on load and would otherwise trip this gate with no shopper
  // action), and accordion__disclosure elements in OTHER PDP sections (e.g. FAQ).
  // Delegated at document level (capture phase catches the non-bubbling
  // 'toggle' event), so it is rerender-safe.
  function onPdpDetailsToggle(e) {
    var t = e.target;
    if (!t || t.tagName !== 'DETAILS' || !t.open) return;
    if (!t.classList || !t.classList.contains('accordion__disclosure')) return;
    var sec = t.closest('[id^="shopify-section-"]');
    if (!sec || !sec.querySelector('product-gallery')) return; // product main section only
    engineState.pdp_accordion_opened = true;
  }

  // Page-scoped interaction signal for pdp-classified pages whose gallery and
  // accordions are not <product-gallery> / .accordion__disclosure (the Signet
  // landing, assets/signet-landing.js). Sets the same two one-way flags as the
  // native gates above; publishes nothing. Registered only by the pdp-gated
  // initPdpInteractionTracking().
  function onPageInteractionSignal(e) {
    var kind = e && e.detail && e.detail.kind;
    if (kind === 'gallery') engineState.pdp_carousel_advanced = true;
    else if (kind === 'accordion') engineState.pdp_accordion_opened = true;
  }

  /* ---------- PDP variant-click trigger (combined-listing navigation) ----- */
  // On every Lunburg PDP template, choosing a variant (colour / size / model /
  // profile / interior configuration / thickness / variant) is a combined-listing
  // <a href="/products/..."> swatch inside .variant-picker__option-values that
  // NAVIGATES to a separate product (verified across all 7 templates; there are
  // zero in-place Shopify variants). A click therefore unloads the page, so
  // publishing during unload is unreliable (the sandboxed Custom Pixel subscriber
  // is async and the page unloads first).
  //
  // Mechanism: record the intent synchronously to sessionStorage on click
  // (setItem completes before unload), then re-emit engaged_page_view on the NEXT
  // page load — the destination PDP, where delivery is reliable. Attribution to
  // the source product is intentionally NOT required. Modified clicks
  // (cmd/ctrl/shift/alt) open a new tab and do NOT unload this page, so we publish
  // immediately in that case. Delegated at document level (capture) so it runs
  // before navigation and survives <product-rerender> partial rerenders.

  var VARIANT_PENDING_KEY = 'lunburg_pending_engaged_pv';
  var VARIANT_PENDING_TTL_MS = 60000;

  function fireEngagedPageViewNow() {
    if (engineState.page_view_fired || alreadyFired('engaged_page_view')) return;
    engineState.page_view_fired = true;
    markFired('engaged_page_view');
    publishLunburgEvent('engaged_page_view', {
      engagement_time_ms: engineState.engaged_time_ms,
      max_scroll_depth: engineState.max_scroll_depth,
      engagement_page_type: currentEngagementPageType()
    });
  }

  function onVariantSwatchClick(e) {
    var a = e.target && typeof e.target.closest === 'function'
      ? e.target.closest('.variant-picker__option-values a[href]')
      : null;
    if (!a) return;
    if ((a.getAttribute('href') || '').indexOf('/products/') === -1) return;
    // Re-selecting the current option (same product URL) is a no-op — skip.
    try {
      if (new URL(a.href, window.location.origin).pathname === window.location.pathname) return;
    } catch (err) { /* fall through */ }
    if (a.classList.contains('is-selected') || a.getAttribute('aria-current') === 'true') return;
    var sec = a.closest('[id^="shopify-section-"]');
    if (!sec || !sec.querySelector('product-gallery')) return; // main product area only

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      // Opens a new tab/window; THIS page stays loaded — publish immediately.
      fireEngagedPageViewNow();
      return;
    }
    // Same-tab navigation: stash the intent now (synchronous, before unload) and
    // emit on the next page load. NOT gated on this page's fired state — the
    // carried event is for the DESTINATION page, so each variant navigation
    // triggers exactly one engaged_page_view (on the page it lands on).
    try {
      sessionStorage.setItem(VARIANT_PENDING_KEY, JSON.stringify({
        ts: Date.now(),
        engaged: engineState.engaged_time_ms,
        scroll: engineState.max_scroll_depth
      }));
    } catch (err) {
      // sessionStorage unavailable — best-effort immediate publish for this page.
      fireEngagedPageViewNow();
    }
  }

  // Drained on the next page load: if we arrived via a variant-swatch click on the
  // previous PDP, emit the carried engaged_page_view now (on this stable page).
  // One-shot (removed on read) + TTL guard against stale intents + standard dedup.
  function drainPendingEngagedPageView() {
    var raw;
    try { raw = sessionStorage.getItem(VARIANT_PENDING_KEY); } catch (err) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(VARIANT_PENDING_KEY); } catch (err) { /* ignore */ }
    var p;
    try { p = JSON.parse(raw); } catch (err) { return; }
    if (!p || typeof p.ts !== 'number') return;
    if (Date.now() - p.ts >= VARIANT_PENDING_TTL_MS) return; // stale intent — drop
    if (engineState.page_view_fired || alreadyFired('engaged_page_view')) return;
    engineState.page_view_fired = true;
    markFired('engaged_page_view');
    publishLunburgEvent('engaged_page_view', {
      engagement_time_ms: typeof p.engaged === 'number' ? p.engaged : engineState.engaged_time_ms,
      max_scroll_depth: typeof p.scroll === 'number' ? p.scroll : engineState.max_scroll_depth,
      engagement_page_type: currentEngagementPageType()
    });
  }

  function initPdpInteractionTracking() {
    if (currentEngagementPageType() !== 'pdp') return;
    // If we arrived here via a variant-swatch click on the previous PDP, emit the
    // carried engaged_page_view now (this page is loaded and stable).
    drainPendingEngagedPageView();
    document.addEventListener('pointerdown', noteGalleryInteraction, true);
    document.addEventListener('keydown', noteGalleryInteraction, true);
    document.addEventListener('toggle', onPdpDetailsToggle, true);
    document.addEventListener('click', onVariantSwatchClick, true);
    document.addEventListener('lb:pdp-interaction', onPageInteractionSignal);
    wireGalleryCarousels();
    var anchor = document.querySelector('product-gallery');
    var pdpRoot = (anchor && typeof anchor.closest === 'function'
      ? anchor.closest('[id^="shopify-section-"]')
      : null) || document.body;
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(wireGalleryCarousels).observe(pdpRoot, { childList: true, subtree: true });
    }
    if (window.LunburgAnalyticsDebug) {
      // eslint-disable-next-line no-console
      console.log('[lunburg behavior-events] PDP interaction gates init');
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdpInteractionTracking);
  } else {
    initPdpInteractionTracking();
  }

  /* 1Hz heartbeat: scroll refresh, engaged-time accumulation, fire engaged_*. */
  updateScrollDepth(); // initialize once on load (handles short pages / first viewport)
  setInterval(function () {
    updateScrollDepth(); // also refresh inside tick — covers no-scroll-events cases
    var engaged = isCurrentlyEngaged();
    if (engaged) {
      engineState.engaged_time_ms += 1000;
    }

    // ---- engaged_page_view (Wave 2 + PDP interaction gates) ----
    if (!engineState.page_view_fired) {
      var pt = currentEngagementPageType();
      var thr = thresholdsForCurrentPage();
      // engaged_page_view fires on engaged time alone for ALL page types.
      // The scroll-depth gate was removed 2026-06-01 per request; scroll is
      // still measured and carried in max_scroll_depth, just not required.
      // PDP extension (2026-06-08): on product pages the event may also fire at
      // the lower interaction_engaged_ms threshold once the shopper has advanced
      // a gallery photo (pdp_carousel_advanced) or expanded a product-section
      // accordion (pdp_accordion_opened). A variant-swatch click fires it via the
      // carry-over path (see "PDP variant-click trigger"), not this heartbeat.
      var shouldFirePageView = false;
      if (engineState.engaged_time_ms >= thr.engaged_ms) {
        shouldFirePageView = true;
      } else if (pt === 'pdp'
          && typeof thr.interaction_engaged_ms === 'number'
          && engineState.engaged_time_ms >= thr.interaction_engaged_ms
          && (engineState.pdp_carousel_advanced || engineState.pdp_accordion_opened)) {
        shouldFirePageView = true;
      }
      if (shouldFirePageView && !alreadyFired('engaged_page_view')) {
        engineState.page_view_fired = true;
        markFired('engaged_page_view');
        publishLunburgEvent('engaged_page_view', {
          engagement_time_ms: engineState.engaged_time_ms,
          max_scroll_depth: engineState.max_scroll_depth,
          engagement_page_type: pt
        });
      }
    }

    // ---- engaged_accordion_open (Wave 3) ----
    // DUAL gate: engaged_ms (tick-counter) >= 3000 AND wall-clock since open >= 3000.
    // The wall-clock guard ensures time_open_ms in the payload is never < 3000.
    var nowTs = Date.now();
    var aid;
    for (aid in engineState.open_accordions) {
      if (!Object.prototype.hasOwnProperty.call(engineState.open_accordions, aid)) continue;
      var a = engineState.open_accordions[aid];
      if (a.fired) continue;
      if (engaged) a.engaged_ms += 1000;
      if (a.engaged_ms >= ACCORDION_DWELL_MS
          && (nowTs - a.opened_ts) >= ACCORDION_DWELL_MS
          && !alreadyFired('engaged_accordion_open', aid)) {
        a.fired = true;
        markFired('engaged_accordion_open', aid);
        publishLunburgEvent('engaged_accordion_open', {
          accordion_id: aid,
          accordion_label: a.label,
          accordion_class: a.class,
          time_open_ms: nowTs - a.opened_ts
        });
      }
    }

    // ---- engaged_section_view (Wave 3) ----
    var sid;
    for (sid in engineState.observed_sections) {
      if (!Object.prototype.hasOwnProperty.call(engineState.observed_sections, sid)) continue;
      var sec = engineState.observed_sections[sid];
      if (sec.fired) continue;
      if (engaged && sec.last_visible_pct >= SECTION_VISIBLE_THRESHOLD_PCT) {
        sec.engaged_ms += 1000;
      }
      if (sec.engaged_ms >= SECTION_DWELL_MS && !alreadyFired('engaged_section_view', sid)) {
        sec.fired = true;
        markFired('engaged_section_view', sid);
        publishLunburgEvent('engaged_section_view', {
          section_id: sid,
          section_name: sec.section_name,
          visible_pct: sec.last_visible_pct,
          engagement_time_ms: sec.engaged_ms
        });
      }
    }
  }, 1000);
})();
